import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { buildPublicProfileMap } from '@/services/profileUtils';
import type {
  ForjaPartner,
  ForjaPartnerMember,
  ForjaPartnerLink,
  ForjaPartnerAnnouncement,
  PartnerWithMembership,
  PartnerMemberWithProfile,
  CreatePartnerParams,
  UpdatePartnerParams,
  CreateLinkParams,
  UpdateLinkParams,
  CreateAnnouncementParams,
  UpdateAnnouncementParams,
  PartnerAdminMetrics,
  generateSlug,
} from '@/types/partners';
import { createBulkNotifications } from '@/services/notifications';

type PartnerUpdate = Database['public']['Tables']['forja_partners']['Update'];

// ======================
// PARTNERS (READ)
// ======================

// Optimized partner list columns
// NOTE: `email` is intentionally omitted — column-level grants block non-admin
// SELECT on that column. Admin access goes through the get_partner_emails RPC.
const PARTNER_LIST_COLUMNS = `
  id, name, slug, description, logo_url, banner_url, partner_admin_id,
  entry_mode, is_active, support_url, tiktok_shop_url, created_at, updated_at, created_by
`;

// Optimized membership columns
const MEMBERSHIP_COLUMNS = `id, partner_id, user_id, status, terms_accepted_at, requested_at, reviewed_at, reviewed_by`;

export async function fetchActivePartners(): Promise<PartnerWithMembership[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  // Fetch active partners
  const { data: partners, error } = await supabase
    .from('forja_partners')
    .select(PARTNER_LIST_COLUMNS)
    .eq('is_active', true)
    .order('name');

  if (error) {
    console.error('Error fetching partners:', error);
    throw error;
  }

  // Fetch user's memberships
  const { data: memberships } = await supabase
    .from('forja_partner_members')
    .select(MEMBERSHIP_COLUMNS)
    .eq('user_id', user.id);

  const membershipMap = new Map(memberships?.map(m => [m.partner_id, m]) || []);

  return (partners || []).map(p => ({
    ...p,
    entry_mode: p.entry_mode as 'open' | 'approval',
    membership: membershipMap.get(p.id) as ForjaPartnerMember | null || null,
  }));
}

export async function fetchPartnerBySlug(slug: string): Promise<PartnerWithMembership | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: partner, error } = await supabase
    .from('forja_partners')
    .select(PARTNER_LIST_COLUMNS)
    .eq('slug', slug)
    .maybeSingle();

  if (error) {
    console.error('Error fetching partner:', error);
    throw error;
  }

  if (!partner) return null;

  // Check if partner is active or user is partner admin
  if (!partner.is_active && partner.partner_admin_id !== user.id) {
    return null;
  }

  // Get user's membership
  const { data: membership } = await supabase
    .from('forja_partner_members')
    .select(MEMBERSHIP_COLUMNS)
    .eq('partner_id', partner.id)
    .eq('user_id', user.id)
    .maybeSingle();

  return {
    ...partner,
    entry_mode: partner.entry_mode as 'open' | 'approval',
    membership: membership as ForjaPartnerMember | null,
  };
}

// ======================
// MEMBERSHIP
// ======================

export async function joinPartner(partnerId: string, entryMode: 'open' | 'approval'): Promise<ForjaPartnerMember> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Não autenticado');

  const status = entryMode === 'open' ? 'approved' : 'pending';

  const { data, error } = await supabase
    .from('forja_partner_members')
    .insert({
      partner_id: partnerId,
      user_id: user.id,
      status,
      terms_accepted_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) {
    console.error('Error joining partner:', error);
    throw error;
  }

  return data as ForjaPartnerMember;
}

export async function leavePartner(partnerId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Não autenticado');

  const { error } = await supabase
    .from('forja_partner_members')
    .delete()
    .eq('partner_id', partnerId)
    .eq('user_id', user.id);

  if (error) {
    console.error('Error leaving partner:', error);
    throw error;
  }
}

export async function fetchMyMembership(partnerId: string): Promise<ForjaPartnerMember | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('forja_partner_members')
    .select(MEMBERSHIP_COLUMNS)
    .eq('partner_id', partnerId)
    .eq('user_id', user.id)
    .maybeSingle();

  if (error && error.code !== 'PGRST116') {
    console.error('Error fetching membership:', error);
    throw error;
  }

  return data as ForjaPartnerMember | null;
}

// ======================
// LINKS (READ)
// ======================

// Optimized link columns
const LINK_COLUMNS = `id, partner_id, title, url, description, display_order, created_at`;

export async function fetchPartnerLinks(partnerId: string): Promise<ForjaPartnerLink[]> {
  const { data, error } = await supabase
    .from('forja_partner_links')
    .select(LINK_COLUMNS)
    .eq('partner_id', partnerId)
    .order('display_order');

  if (error) {
    console.error('Error fetching links:', error);
    throw error;
  }

  return (data || []) as ForjaPartnerLink[];
}

// ======================
// ANNOUNCEMENTS (READ)
// ======================

// Optimized announcement columns
const ANNOUNCEMENT_COLUMNS = `id, partner_id, title, content, image_url, link_url, created_by, created_at, updated_at`;

export async function fetchPartnerAnnouncements(partnerId: string): Promise<ForjaPartnerAnnouncement[]> {
  const { data, error } = await supabase
    .from('forja_partner_announcements')
    .select(ANNOUNCEMENT_COLUMNS)
    .eq('partner_id', partnerId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching announcements:', error);
    throw error;
  }

  return (data || []) as ForjaPartnerAnnouncement[];
}

// ======================
// PARTNER ADMIN: MEMBERS
// ======================

export async function fetchPartnerMembers(partnerId: string): Promise<PartnerMemberWithProfile[]> {
  const { data, error } = await supabase
    .from('forja_partner_members')
    .select(MEMBERSHIP_COLUMNS)
    .eq('partner_id', partnerId)
    .order('requested_at', { ascending: false });

  if (error) {
    console.error('Error fetching members:', error);
    throw error;
  }

  // Enrich with profile data
  const userIds = (data || []).map(m => m.user_id);
  if (userIds.length === 0) return [];

  const profileMapObj = await buildPublicProfileMap(userIds);

  return (data || []).map(m => ({
    ...m,
    status: m.status as 'pending' | 'approved' | 'rejected',
    display_name: profileMapObj[m.user_id]?.display_name || null,
    avatar_url: profileMapObj[m.user_id]?.avatar_url || null,
  }));
}

export async function updateMemberStatus(
  memberId: string,
  status: 'approved' | 'rejected'
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Não autenticado');

  const { error } = await supabase
    .from('forja_partner_members')
    .update({
      status,
      reviewed_at: new Date().toISOString(),
      reviewed_by: user.id,
    })
    .eq('id', memberId);

  if (error) {
    console.error('Error updating member status:', error);
    throw error;
  }
}

export async function removeMember(memberId: string): Promise<void> {
  const { error } = await supabase
    .from('forja_partner_members')
    .delete()
    .eq('id', memberId);

  if (error) {
    console.error('Error removing member:', error);
    throw error;
  }
}

// ======================
// PARTNER ADMIN: PARTNER INFO
// ======================

export async function updatePartnerInfo(partnerId: string, params: UpdatePartnerParams): Promise<void> {
  const { error } = await supabase
    .from('forja_partners')
    .update(params)
    .eq('id', partnerId);

  if (error) {
    console.error('Error updating partner:', error);
    throw error;
  }
}

// ======================
// PARTNER ADMIN: LINKS
// ======================

export async function createLink(params: CreateLinkParams): Promise<ForjaPartnerLink> {
  const { data, error } = await supabase
    .from('forja_partner_links')
    .insert(params)
    .select()
    .single();

  if (error) {
    console.error('Error creating link:', error);
    throw error;
  }

  return data as ForjaPartnerLink;
}

export async function updateLink(linkId: string, params: UpdateLinkParams): Promise<void> {
  const { error } = await supabase
    .from('forja_partner_links')
    .update(params)
    .eq('id', linkId);

  if (error) {
    console.error('Error updating link:', error);
    throw error;
  }
}

export async function deleteLink(linkId: string): Promise<void> {
  const { error } = await supabase
    .from('forja_partner_links')
    .delete()
    .eq('id', linkId);

  if (error) {
    console.error('Error deleting link:', error);
    throw error;
  }
}

export async function reorderLinks(partnerId: string, linkIds: string[]): Promise<void> {
  // Update each link's display_order based on position in array
  const updates = linkIds.map((id, index) =>
    supabase
      .from('forja_partner_links')
      .update({ display_order: index })
      .eq('id', id)
      .eq('partner_id', partnerId)
  );

  await Promise.all(updates);
}

// ======================
// PARTNER ADMIN: ANNOUNCEMENTS
// ======================

export async function createAnnouncement(
  params: CreateAnnouncementParams
): Promise<ForjaPartnerAnnouncement> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Não autenticado');

  const { data, error } = await supabase
    .from('forja_partner_announcements')
    .insert({
      ...params,
      created_by: user.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating announcement:', error);
    throw error;
  }

  // Send notifications to approved members
  try {
    await notifyAnnouncementCreated(params.partner_id, data as ForjaPartnerAnnouncement);
  } catch (notifError) {
    console.error('Error sending announcement notifications:', notifError);
    // Don't fail the announcement creation
  }

  return data as ForjaPartnerAnnouncement;
}

export async function updateAnnouncement(
  announcementId: string,
  params: UpdateAnnouncementParams
): Promise<void> {
  const { error } = await supabase
    .from('forja_partner_announcements')
    .update(params)
    .eq('id', announcementId);

  if (error) {
    console.error('Error updating announcement:', error);
    throw error;
  }
}

export async function deleteAnnouncement(announcementId: string): Promise<void> {
  const { error } = await supabase
    .from('forja_partner_announcements')
    .delete()
    .eq('id', announcementId);

  if (error) {
    console.error('Error deleting announcement:', error);
    throw error;
  }
}

// ======================
// NOTIFICATIONS
// ======================

async function notifyAnnouncementCreated(
  partnerId: string,
  announcement: ForjaPartnerAnnouncement
): Promise<void> {
  // Get partner info
  const { data: partner } = await supabase
    .from('forja_partners')
    .select('name, slug')
    .eq('id', partnerId)
    .single();

  if (!partner) return;

  // Get approved member user IDs
  const { data: members } = await supabase
    .from('forja_partner_members')
    .select('user_id')
    .eq('partner_id', partnerId)
    .eq('status', 'approved');

  const userIds = members?.map(m => m.user_id) || [];
  if (userIds.length === 0) return;

  await createBulkNotifications(userIds, {
    type: 'partner',
    title: `📢 Novo aviso de ${partner.name}`,
    message: announcement.title,
    action_url: `/parceiros/${partner.slug}`,
    action_label: 'Ver aviso',
  });
}

// ======================
// FORJAHUB ADMIN
// ======================

export async function fetchAllPartnersForAdmin(): Promise<PartnerAdminMetrics[]> {
  // For admin metrics, we need specific fields
  const { data: partners, error } = await supabase
    .from('forja_partners')
    .select('id, name, slug, logo_url, partner_admin_id, entry_mode, is_active, created_at')
    .order('name');

  if (error) {
    console.error('Error fetching partners for admin:', error);
    throw error;
  }

  if (!partners || partners.length === 0) return [];

  // Fetch partner emails via secure admin-only RPC (column-level RLS hides
  // `email` from the broad SELECT — only admins can read it through this RPC).
  const partnerIds = partners.map((p) => p.id);
  const { data: emailRows } = await supabase.rpc('get_partner_emails', {
    _partner_ids: partnerIds,
  });
  const emailMap = new Map<string, string | null>(
    (emailRows ?? []).map((row: { id: string; email: string | null }) => [row.id, row.email]),
  );

  // Get admin profiles
  const adminIds = partners.map(p => p.partner_admin_id);
  const adminProfileMap = await buildPublicProfileMap(adminIds);
  const profileMap = new Map(
    Object.entries(adminProfileMap).map(([id, p]) => [id, p.display_name])
  );

  // Get member counts
  const { data: memberCounts } = await supabase
    .from('forja_partner_members')
    .select('partner_id, status');

  const countMap = new Map<string, { approved: number; pending: number }>();
  for (const m of memberCounts || []) {
    const existing = countMap.get(m.partner_id) || { approved: 0, pending: 0 };
    if (m.status === 'approved') existing.approved++;
    if (m.status === 'pending') existing.pending++;
    countMap.set(m.partner_id, existing);
  }

  // Get last announcement dates
  const { data: lastAnnouncements } = await supabase
    .from('forja_partner_announcements')
    .select('partner_id, created_at')
    .in('partner_id', partnerIds)
    .order('created_at', { ascending: false });

  const lastAnnouncementMap = new Map<string, string>();
  for (const a of lastAnnouncements || []) {
    if (!lastAnnouncementMap.has(a.partner_id)) {
      lastAnnouncementMap.set(a.partner_id, a.created_at);
    }
  }

  // Get campaign metrics
  const { data: campaigns } = await supabase
    .from('forja_partner_campaigns')
    .select('partner_id, status, is_featured, name')
    .in('partner_id', partnerIds);

  const campaignMetrics = new Map<string, { active: number; ended: number; featuredName: string | null }>();
  for (const c of campaigns || []) {
    const existing = campaignMetrics.get(c.partner_id) || { active: 0, ended: 0, featuredName: null };
    if (c.status === 'active') existing.active++;
    if (c.status === 'ended') existing.ended++;
    if (c.is_featured && c.status === 'active') existing.featuredName = c.name;
    campaignMetrics.set(c.partner_id, existing);
  }

  return partners.map(p => ({
    id: p.id,
    name: p.name,
    slug: p.slug,
    email: emailMap.get(p.id) ?? null,
    is_active: p.is_active,
    partner_admin_id: p.partner_admin_id,
    partner_admin_name: profileMap.get(p.partner_admin_id) || null,
    member_count: countMap.get(p.id)?.approved || 0,
    pending_count: countMap.get(p.id)?.pending || 0,
    last_announcement_at: lastAnnouncementMap.get(p.id) || null,
    active_campaigns_count: campaignMetrics.get(p.id)?.active || 0,
    ended_campaigns_count: campaignMetrics.get(p.id)?.ended || 0,
    featured_campaign_name: campaignMetrics.get(p.id)?.featuredName || null,
  }));
}

export async function createPartner(params: CreatePartnerParams): Promise<ForjaPartner> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Não autenticado');

  // Generate slug from name
  const slug = params.name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const { data, error } = await supabase
    .from('forja_partners')
    .insert({
      name: params.name,
      slug,
      partner_admin_id: params.partner_admin_id,
      is_active: params.is_active ?? true,
      created_by: user.id,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating partner:', error);
    throw error;
  }

  return data as ForjaPartner;
}

export async function togglePartnerActive(partnerId: string, isActive: boolean): Promise<void> {
  const { error } = await supabase
    .from('forja_partners')
    .update({ is_active: isActive })
    .eq('id', partnerId);

  if (error) {
    console.error('Error toggling partner active:', error);
    throw error;
  }
}

export async function updatePartnerAdmin(partnerId: string, newAdminId: string): Promise<void> {
  const { error } = await supabase
    .from('forja_partners')
    .update({ partner_admin_id: newAdminId })
    .eq('id', partnerId);

  if (error) {
    console.error('Error updating partner admin:', error);
    throw error;
  }
}

// Update partner info by ForjaHub admin (name, email)
export async function updatePartnerByAdmin(
  partnerId: string, 
  params: { name?: string; email?: string }
): Promise<void> {
  const updateData: PartnerUpdate = {};
  
  if (params.name !== undefined) {
    updateData.name = params.name;
    // Regenerate slug if name changed
    updateData.slug = params.name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
  
  if (params.email !== undefined) {
    updateData.email = params.email;
  }

  const { error } = await supabase
    .from('forja_partners')
    .update(updateData)
    .eq('id', partnerId);

  if (error) {
    console.error('Error updating partner by admin:', error);
    throw error;
  }
}

// ======================
// USER SEARCH (for admin)
// ======================

export interface SearchedUserForPartner {
  user_id: string;
  display_name: string | null;
}

export async function searchUsersForPartnerAdmin(query: string): Promise<SearchedUserForPartner[]> {
  if (!query || query.length < 2) return [];

  const searchMembers = supabase.rpc as unknown as (
    name: 'search_community_members',
    args: { p_query: string; p_limit: number },
  ) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
  const { data, error } = await searchMembers('search_community_members', {
    p_query: query,
    p_limit: 10,
  });

  if (error) {
    console.error('Error searching users:', error);
    throw error;
  }

  return Array.isArray(data) ? data as SearchedUserForPartner[] : [];
}

// Check if user has access to partner content
export function hasAccessToPartnerContent(
  partner: PartnerWithMembership,
  userId: string
): boolean {
  // Partner admin always has access
  if (partner.partner_admin_id === userId) return true;
  
  // Open entry mode = anyone can view
  if (partner.entry_mode === 'open') return true;
  
  // Approval mode = need approved membership
  return partner.membership?.status === 'approved';
}

// ======================
// PARTNER ADMIN: MEMBER TIKTOK USERNAMES
// ======================

/**
 * Fetch only the @usernames of TikTok accounts for a member.
 * Partner admins can only see usernames, not other account details.
 */
export async function fetchMemberTikTokUsernames(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('partner_member_tiktok_usernames')
    .select('username')
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching member TikTok usernames:', error);
    return [];
  }

  return (data || [])
    .map((account) => account.username)
    .filter((username): username is string => Boolean(username));
}
